import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { StorefrontWhatsApp } from '@/features/storefront/api/storefront';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { whatsAppShareUrl, whatsAppUrl } from '@/lib/whatsapp';

/** What a "Buy on WhatsApp" button says it is about. */
export interface WhatsAppBuyTarget {
  name: string;
  /** The product page, absolute, so the merchant can tap straight to it. */
  url: string;
  /** The chosen options, written out - `Size M, Colour Indigo`. */
  variantLabel?: string;
}

export interface StorefrontWhatsAppLinks {
  /** The channel as the context describes it, or null for a shop without one. */
  whatsapp: StorefrontWhatsApp | null;
  /** True when the merchant has a connected number the shop may point at. */
  enabled: boolean;
  /** A chat with the merchant, `message` already typed. Null with no number. */
  chatUrl: (message?: string) => string | null;
  /** A chat asking the merchant for one product. Null with no number. */
  buyUrl: (target: WhatsAppBuyTarget) => string | null;
  /** WhatsApp with `text` typed and the recipient left to the shopper. */
  shareUrl: (text: string) => string;
}

/** The `{{product_name}}` in a merchant's prefill, filled in. */
function fillPrefill(template: string, name: string): string {
  return template.replace(/\{\{\s*product_name\s*\}\}/g, name);
}

/**
 * The links a storefront draws for the merchant's WhatsApp channel.
 *
 * Every consumer asks this rather than reading the context itself, so the
 * question "does this shop have a number to chat with" has one answer and the
 * pages cannot disagree about it. Null-safe throughout: a shop with no channel
 * gets `enabled: false` and null links, and a component that renders on that is
 * a component with a bug in it, not a page that white-screens.
 *
 * `override` is for the shell, which already holds the context and must not
 * ask for it a second time under a different key; everything under the shell
 * reads the same context the pages do.
 */
export function useStorefrontWhatsApp(
  override?: StorefrontWhatsApp | null,
): StorefrontWhatsAppLinks {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const context = useQuery({
    queryKey: [...scope, 'context'],
    queryFn: () => source.context(),
    enabled: override === undefined,
  });

  const whatsapp = override !== undefined ? override : (context.data?.whatsapp ?? null);

  return useMemo(() => {
    const phone = whatsapp?.enabled ? whatsapp.phone.replace(/\D/g, '') : '';
    const enabled = phone.length > 0;

    const chatUrl = (message?: string): string | null => {
      if (!enabled || !whatsapp) return null;
      if (message && message.trim()) return whatsAppUrl(phone, message);
      return whatsapp.chat_url ?? `https://wa.me/${phone}`;
    };

    const buyUrl = (target: WhatsAppBuyTarget): string | null => {
      if (!enabled || !whatsapp) return null;
      const opening = fillPrefill(whatsapp.buy_prefill, target.name).trim();
      const named = target.variantLabel ? `${target.name} (${target.variantLabel})` : target.name;
      // The merchant's own opening line, then the product named and linked on
      // lines of their own: the name is what the merchant reads, the link is
      // what they tap to see which one.
      const lines = [opening || `Hi! I'd like to buy ${named}.`];
      if (opening) lines.push(named);
      lines.push(target.url);
      return whatsAppUrl(phone, lines.join('\n'));
    };

    return { whatsapp, enabled, chatUrl, buyUrl, shareUrl: whatsAppShareUrl };
  }, [whatsapp]);
}

/**
 * Whether the booking form may offer "send me updates on WhatsApp".
 *
 * True only when the server says the shop sends appointment messages there -
 * a connected number *and* at least one appointment message switched on - so
 * a shopper is never asked to opt in to messages nobody will send. A context
 * still loading, refused, or from an older server reads as no.
 *
 * `enabled: false` asks nothing - the customiser's preview, which books
 * nothing and must not reach for the live shop, or a section with no phone.
 */
export function useBookingWhatsApp(enabled = true): boolean {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const context = useQuery({
    queryKey: [...scope, 'context'],
    queryFn: () => source.context(),
    enabled,
  });
  return enabled && context.data?.booking_whatsapp === true;
}
